// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/jobs/model/lime_buffer.py.
// Change both and update the parity fixtures in shared/vectors/tristate_lime_inmi.json.
// Why: the public API builds Rx on the server (ADR 0003 and ADR 0023).
// CI runs this table in frontend/src/lib/limeBuffer.test.ts and the server copy.
// ============================================================================
// Culman, S., et al. 2020. Tri-State Fertilizer Recommendations for Corn,
// Soybean, Wheat, and Alfalfa. Bulletin 974, The Ohio State University (with
// Purdue and Michigan State). Table 2, printed page 14 (PDF page 16),
// "Indiana and Michigan Liming Rates for Mineral Soils".
// https://trumbull.osu.edu/sites/trumbull/files/imce/Program_Pages/ANR/Tri%20State%20Fertility%20Guide%202021.pdf
// Tons of material assuming a neutralizing value of 90% (Michigan) or a
// relative neutralizing value of 65 (Indiana). Buffer pH is SMP-equivalent.
// No depth conversion. Cells are the printed table. Buffer 6.0 / target 6.5
// is 9.2 (the table, not the footnote equation's 9.1). Footnote equations
// are used only above buffer pH 6.9.

/** The published tons are already 90% NV material. The Rx stores them as-is. */
export const TRISTATE_NV_BASIS_PCT = 90;

export const TRISTATE_BUFFER = [
  6.9, 6.8, 6.7, 6.6, 6.5, 6.4, 6.3, 6.2, 6.1, 6.0,
] as const;

export const TRISTATE_TARGETS = [6.0, 6.5, 6.8] as const;

export const TRISTATE_TONS: readonly (readonly [number, number, number])[] = [
  [0.4, 0.6, 0.8],
  [1.2, 1.6, 1.8],
  [1.9, 2.5, 2.9],
  [2.7, 3.5, 3.9],
  [3.5, 4.4, 4.9],
  [4.3, 5.3, 5.9],
  [5.1, 6.3, 6.9],
  [5.8, 7.2, 8.0],
  [6.6, 8.2, 9.0],
  [7.4, 9.2, 10.0],
];

export const TRISTATE_EQUATIONS = [
  { target: 6.0, intercept: 54.2, slope: 0.78 },
  { target: 6.5, intercept: 65.5, slope: 0.94 },
  { target: 6.8, intercept: 71.2, slope: 1.02 },
] as const;

export const TRISTATE_TARGET_MIN = 6;
export const TRISTATE_TARGET_MAX = 6.8;
export const BUFFER_PH_LAYER_ID = "ml-bph";

export type BphFlag = "bph_below_table";

export interface TristateTons {
  tons: number;
  flag?: BphFlag;
}

export class LimeTargetOutOfRangeError extends Error {
  readonly code = "lime_target_out_of_range" as const;
  readonly min = TRISTATE_TARGET_MIN;
  readonly max = TRISTATE_TARGET_MAX;
  readonly method = "tristate_buffer" as const;

  constructor(target: number) {
    super(`lime_target_out_of_range: target pH ${target} is outside 6.0–6.8`);
    this.name = "LimeTargetOutOfRangeError";
  }
}

export function targetInTristateRange(target: number): boolean {
  return (
    Number.isFinite(target) &&
    target >= TRISTATE_TARGET_MIN - 1e-9 &&
    target <= TRISTATE_TARGET_MAX + 1e-9
  );
}

type Col = 0 | 1 | 2;

function tonsAtTenths(tenths: number, col: Col): number {
  const row = TRISTATE_TONS[69 - tenths];
  if (!row) throw new Error(`buffer pH tenths ${tenths} are outside the table`);
  return row[col];
}

function equationTons(bph: number, col: Col): number {
  const eq = TRISTATE_EQUATIONS[col];
  return Math.max(0, eq.intercept - eq.slope * (bph * 10));
}

function columnAtBph(bph: number, col: Col): TristateTons {
  if (bph < TRISTATE_TARGET_MIN) {
    return { tons: tonsAtTenths(60, col), flag: "bph_below_table" };
  }
  if (bph > 6.9) return { tons: equationTons(bph, col) };
  const scaled = bph * 10;
  const nearest = Math.round(scaled);
  if (Math.abs(scaled - nearest) < 1e-6)
    return { tons: tonsAtTenths(nearest, col) };
  const lo = Math.floor(scaled);
  const t = scaled - lo;
  return {
    tons: tonsAtTenths(lo, col) * (1 - t) + tonsAtTenths(lo + 1, col) * t,
  };
}

function mixColumns(
  target: number,
  cols: readonly [number, number, number],
): number {
  // The knots are the published columns. Float division of 0.3 does not land on 6.8.
  if (Math.abs(target - TRISTATE_TARGET_MIN) < 1e-9) return cols[0];
  if (Math.abs(target - 6.5) < 1e-9) return cols[1];
  if (Math.abs(target - TRISTATE_TARGET_MAX) < 1e-9) return cols[2];
  if (target <= 6.5) {
    const t = (target - TRISTATE_TARGET_MIN) / 0.5;
    return cols[0] + (cols[1] - cols[0]) * t;
  }
  const t = (target - 6.5) / 0.3;
  return cols[1] + (cols[2] - cols[1]) * t;
}

/** Tons of 90% NV lime. Throws when the target is outside 6.0–6.8. NaN buffer pH → NaN. */
export function tristateTons(bph: number, target: number): TristateTons {
  if (!targetInTristateRange(target))
    throw new LimeTargetOutOfRangeError(target);
  if (!Number.isFinite(bph)) return { tons: Number.NaN };
  let flag: BphFlag | undefined;
  const cols = ([0, 1, 2] as const).map((col) => {
    const hit = columnAtBph(bph, col);
    if (hit.flag) flag = hit.flag;
    return hit.tons;
  }) as [number, number, number];
  return { tons: mixColumns(target, cols), ...(flag ? { flag } : {}) };
}

export function manifestHasBufferPh(layers: { id: string }[]): boolean {
  return layers.some((layer) => layer.id === BUFFER_PH_LAYER_ID);
}

export function phTargetBounds(bufferActive: boolean): {
  min: number;
  max: number;
  step: number;
} {
  return bufferActive
    ? { min: TRISTATE_TARGET_MIN, max: TRISTATE_TARGET_MAX, step: 0.05 }
    : { min: 4, max: 8.5, step: 0.05 };
}

export function limeTargetError(
  target: number,
  bufferActive: boolean,
): string | null {
  if (!bufferActive) return null;
  if (targetInTristateRange(target)) return null;
  return "Target pH must be between 6.0 and 6.8 for the Tri-State buffer pH table.";
}

export function pinPhTarget(
  target: number,
  bufferActive: boolean,
): { value: number; notice: string | null } {
  if (
    !bufferActive ||
    !Number.isFinite(target) ||
    targetInTristateRange(target)
  ) {
    return { value: target, notice: null };
  }
  const value = Math.min(
    TRISTATE_TARGET_MAX,
    Math.max(TRISTATE_TARGET_MIN, target),
  );
  return {
    value,
    notice: `Target pH changed from ${target} to ${value.toFixed(1)} to fit the Tri-State buffer table (6.0–6.8).`,
  };
}

export interface LimeFieldMethod {
  name: string;
  method: "buffer" | "water";
}

export function limeFieldMethod(
  hasMap: boolean,
  buildup: boolean,
): "buffer" | "water" {
  return buildup && hasMap ? "buffer" : "water";
}

export function limeMethodBanner(rows: LimeFieldMethod[]): string {
  const buffer = rows
    .filter((row) => row.method === "buffer")
    .map((row) => row.name);
  const water = rows
    .filter((row) => row.method === "water")
    .map((row) => row.name);
  const parts: string[] = [];
  if (buffer.length) parts.push(`Tri-State buffer pH: ${buffer.join(", ")}`);
  if (water.length) parts.push(`Water pH + CEC: ${water.join(", ")}`);
  return parts.join(". ");
}
